import { Formik, Form, Field } from "formik";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import ButtonLoader from "@/Components/ButtonLoader";
import FieldErrorMessage from "@/Components/common/FieldErrorMessage";
import ModalPopUp from "@/Components/common/ModalPopUp";
import { RequiredSymbol } from "@/Components/common/RequiredSymbol";
import { RejectIcon } from "@/Components/Icons/RejectIcon";
import {
  checkInvitedBankedCandidatesOrNot,
  toggleInvitedModal,
} from "@/reducers/client_candidates/candidatesinvited";
import { validateEmail } from "@/validations/auth/login";

/**
 * Renders the InviteCandidateModal component.
 *
 * @return {JSX.Element} The rendered JSX element
 */
const InviteCandidateModal = () => {
  const dispatch = useDispatch();
  const { showInvitedModal, filters, pagination } = useSelector(
    (state) => state.clientCandidatesInvited,
  );
  const { isPremiumAccount } = useSelector((state) => state.session);

  // go back so
  useEffect(() => {
    return () => {
      dispatch(toggleInvitedModal(false));
    };
  }, []);

  const handleClose = () => {
    dispatch(toggleInvitedModal(false));
  };

  return (
    <ModalPopUp
      show={showInvitedModal}
      panelClass="max-w-[770px] max-sm:max-w-[400px]"
      onClose={handleClose}
    >
      <div className="flex flex-col gap-4">
        <div className="flex justify-between">
          <h3 className="text-[20px] font-semibold leading-[normal] text-black">
            Invite New Candidate
          </h3>
          <button
            onClick={handleClose}
            className="text-red outline-none ring-0"
          >
            <RejectIcon />
          </button>
        </div>
        <div>
          <Formik
            initialValues={{
              name: "",
              email: "",
              phoneNumber: "",
            }}
            validateOnChange={false}
            validateOnBlur={false}
            validateOnMount={false}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(true);
              dispatch(
                checkInvitedBankedCandidatesOrNot({
                  ...values,
                  setSubmitting,
                  isPremiumAccount,
                }),
              );
            }}
          >
            {({
              submitForm,
              isSubmitting,
              errors,
              touched,
              isValid,
              values,
            }) => (
              <Form>
                <div className="flex flex-col gap-6">
                  <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
                    <div className="grid gap-3">
                      <label
                        htmlFor="name"
                        className="text-[14px] font-medium leading-[normal] text-txtColor"
                      >
                        Name <RequiredSymbol />
                      </label>
                      <div className="flex flex-col">
                        <Field
                          type="text"
                          className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 !outline-none !ring-0 focus:border-primary ${
                            errors.name && touched.name ? "!border-red-500" : ""
                          }`}
                          autoComplete="off"
                          name="name"
                          placeholder="Enter Candidate Name"
                          onKeyUp={(e) => {
                            if (e.keyCode === 13) {
                              submitForm();
                            }
                          }}
                          validate={(value) => {
                            if (!value) {
                              return "Name is required";
                            } else if (!validateEmail(value)) {
                              return "Name is invalid";
                            }
                          }}
                        />
                        <FieldErrorMessage name="name" />
                      </div>
                    </div>
                    <div className="grid gap-3">
                      <label
                        htmlFor="email"
                        className="text-[14px] font-medium leading-[normal] text-txtColor"
                      >
                        Email Address <RequiredSymbol />
                      </label>
                      <div className="flex flex-col">
                        <Field
                          type="email"
                          className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 !outline-none !ring-0 focus:border-primary ${
                            errors.email && touched.email
                              ? "!border-red-500"
                              : ""
                          }`}
                          autoComplete="off"
                          name="email"
                          placeholder="Enter Email Address"
                          onKeyUp={(e) => {
                            if (e.keyCode === 13) {
                              submitForm();
                            }
                          }}
                          validate={(value) => {
                            return validateEmail(value);
                          }}
                          validateOnChange={true}
                        />
                        <FieldErrorMessage name="email" />
                      </div>
                    </div>
                    <div className="grid gap-3">
                      <label
                        htmlFor="phoneNumber"
                        className="text-[14px] font-medium leading-[normal] text-txtColor"
                      >
                        Phone Number
                      </label>
                      <div className="flex flex-col">
                        <Field
                          type="text"
                          className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 !outline-none !ring-0 focus:border-primary ${
                            errors.phoneNumber && touched.phoneNumber
                              ? "!border-red-500"
                              : ""
                          }`}
                          autoComplete="off"
                          name="phoneNumber"
                          placeholder="Enter Phone Number"
                          onKeyUp={(e) => {
                            if (e.keyCode === 13) {
                              submitForm();
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-[20px]">
                    <button
                      type="button"
                      className="transition-linear relative h-[45px] w-[120px] rounded-lg border border-primary bg-primary text-[14px] font-medium text-white hover:bg-none hover:text-primary"
                      onClick={submitForm}
                    >
                      {isSubmitting ? <ButtonLoader /> : <span>Invite</span>}
                    </button>

                    <button
                      type="button"
                      className="transition-linear h-[45px] w-[120px] rounded-lg border border-primary bg-none text-[14px] font-medium text-primary hover:bg-primary hover:text-white"
                      onClick={handleClose}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </ModalPopUp>
  );
};

export default InviteCandidateModal;
